import i18n from 'client/i18n'
import { ColumnTypeEnum } from 'common/components/entities/Column/enums/ColumnTypeEnum'
import MenuInterface from 'common/components/entities/Menu/types/MenuInterface'
import structureTypes from 'common/constants/structureTypes'
import { RedirectionTypesEnum } from 'common/enums/ButtonActionEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import EntityInterface, {
  BaseEntityWithChildIdsInterface,
  EntityInnerItemInterface,
  EntityWithChildIdsInterface,
  EntityWithChildIdsMigrationType,
} from 'common/types/entities/EntityInterface'
import {
  OldEntityInterface,
  OldEntityWithChildIds,
} from 'common/types/entities/OldEntityInterface'
import { EntitiesStructure } from 'common/types/Page'
import { createAttachments } from 'src/shared/entities/attachments/create-attachments'
import { createBlogPostListing } from 'src/shared/entities/blog-post-listing/create-blog-post-listing'
import { createBookingCalendar } from 'src/shared/entities/booking-calendar/create-booking-calendar'
import { createButtonNew } from 'src/shared/entities/button/create-button'
import { createCarousel } from 'src/shared/entities/carousel/create-carousel'
import { createCheckbox } from 'src/shared/entities/checkbox/create-checkbox'
import { createContactUsField } from 'src/shared/entities/contact-us-field/create-contact-us-field'
import { createContentBox } from 'src/shared/entities/content-box/create-content-box'
import { createFaq, createFaqAnswer } from 'src/shared/entities/faq/create-faq'
import { createField } from 'src/shared/entities/field/create-field'
import { createHeadline } from 'src/shared/entities/headline/create-headline'
import { createPaymentButton } from 'src/shared/entities/payment-button/create-payment-button'
import { createRecaptcha } from 'src/shared/entities/recaptcha/create-recaptcha'
import { createRowColumnLayout } from 'src/shared/entities/row/create-row-column-layout'
import { createSalesRedirectionButton } from 'src/shared/entities/sales-redirection-button/create-sales-redirection-button'
import { createSection } from 'src/shared/entities/section/create-section'
import { createShippingFees } from 'src/shared/entities/shipping-fees/create-shipping-fees'
import { createSurvey } from 'src/shared/entities/survey/create-survey'
import { createText } from 'src/shared/entities/text/create-text'
import { createTextArea } from 'src/shared/entities/text-area/create-text-area'
import {
  createUpsellAgreeButton,
  createUpsellDisagreeButton,
} from 'src/shared/entities/upsell-button/create-upsell-button'
import {
  generateHtmlAttrId,
  insertChild,
} from 'src/shared/entities/utils/generate-base-entity'
import { createWebinarCallToAction } from 'src/shared/entities/webinar-call-to-action/create-webinar-call-to-action'
import { createWebinarSessionVideo } from 'src/shared/entities/webinar-session-video/create-webinar-session-video'
import { MasterBlockId } from 'src/shared/types/base-entity-interface'
import { findEntityByType } from 'tools/utils/entityUtils'
import { createCarouselItem } from '../components/entities/Carousel'
import { createBlogPostContentTable } from '../components/entities/ContentTable'
import { createContactUs } from '../pages/contact-us/entities/ContactUs'
import { createContactUsButton } from '../pages/contact-us/entities/contact-us-button'
import * as createStructureOld from './createStructureNew'

export function createCarouselFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
  masterBlockId?: MasterBlockId,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const carousel = createCarousel(descendantEntity.id, masterBlockId)
  const carouselItem = createCarouselItem(carousel.id, masterBlockId)

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, carousel.id, position),
    },
    [carousel.id]: {
      ...carousel,
      childIds: [carouselItem.id],
    },
    [carouselItem.id]: carouselItem,
  }
}

export function createFaqFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const faq = createFaq(descendantEntity.id, container.masterBlockId)
  const fixedT = i18n.getFixedT(locale)
  const faqAnswer = createFaqAnswer(faq.id, container.masterBlockId)
  faqAnswer.title = fixedT('entity_settings.faq_item.default_title')

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, faq.id, position),
    },
    [faq.id]: {
      ...faq,
      childIds: [faqAnswer.id],
    },
    [faqAnswer.id]: { ...faqAnswer, parentId: faq.id },
  }
}

export function createHeadlineFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)
  const headline = createHeadline(
    descendantEntity.id,
    fixedT('entities.text_headline.default_text'),
    descendantEntity.masterBlockId,
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, headline.id, position),
    },
    [headline.id]: headline,
  }
}

export function createUpsellAgreeButtonFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
  masterBlockId?: MasterBlockId,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)
  const upsellAgreeButton = createUpsellAgreeButton(
    descendantEntity.id,
    masterBlockId,
  )
  upsellAgreeButton.text = fixedT('entities.payment_button_agree.default_text')
  upsellAgreeButton.subText = fixedT('entities.button.default_sub_text')

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        upsellAgreeButton.id,
        position,
      ),
    },
    [upsellAgreeButton.id]: upsellAgreeButton,
  }
}

export function createUpsellDisagreeButtonFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
  masterBlockId?: MasterBlockId,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)
  const upsellDisagreeButton = createUpsellDisagreeButton(
    descendantEntity.id,
    masterBlockId,
  )
  upsellDisagreeButton.text = fixedT(
    'entities.payment_button_disagree.default_text',
  )
  upsellDisagreeButton.subText = fixedT('entities.button.default_sub_text')

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        upsellDisagreeButton.id,
        position,
      ),
    },
    [upsellDisagreeButton.id]: upsellDisagreeButton,
  }
}

export function createSalesRedirectionButtonFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
  masterBlockId?: MasterBlockId,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)
  const salesRedirectionButton = createSalesRedirectionButton(
    descendantEntity.id,
    masterBlockId,
  )
  salesRedirectionButton.text = fixedT(
    'entities.sales_redirection_button.default_text',
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        salesRedirectionButton.id,
        position,
      ),
    },
    [salesRedirectionButton.id]: salesRedirectionButton,
  }
}

export function createBlogPostListFromLibrary(
  container: OldEntityWithChildIds,
  locale: string,
  position = 0,
  categoryId?: number,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const blogPostList = createBlogPostListing(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  if (categoryId) {
    blogPostList.category = `${categoryId}`
  }

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        blogPostList.id,
        position,
      ),
    },
    [blogPostList.id]: blogPostList,
  }
}

export function createContentTableFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const fixedT = i18n.getFixedT(locale)

  const blogPostContentTable = createBlogPostContentTable(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )
  blogPostContentTable.headerTitle = fixedT(
    'entities.content_table.default_header_title',
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        blogPostContentTable.id,
        position,
      ),
    },
    [blogPostContentTable.id]: blogPostContentTable,
  }
}

export function createContactUsFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)

  const contactUs = createContactUs(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )
  const textArea = createTextArea(contactUs.id, contactUs.masterBlockId)
  textArea.placeholder = fixedT('entities.textarea.contact_us_placeholder')

  const { row, column1, column2 } = createRowColumnLayout({
    parentEntity: contactUs,
    position,
    columnType: ColumnTypeEnum.COLUMN_6,
  })

  row.padding = {
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  }
  row.mobilePadding = {
    paddingBottom: 5,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 5,
  }

  const firstNameField = createContactUsField(
    column1.id,
    column1.masterBlockId,
    FieldSlugEnum.FirstName,
  )
  firstNameField.placeholder = fixedT(
    'entities.field.contact_us_first_name_placeholder',
  )

  // todo check should be column1 or column2???
  const subjectField = createContactUsField(
    contactUs.id,
    contactUs.masterBlockId,
    FieldSlugEnum.Subject,
  )
  subjectField.placeholder = fixedT('entities.subject.contact_us_placeholder')

  const emailField = createContactUsField(
    column2.id,
    column2.masterBlockId,
    FieldSlugEnum.Email,
  )
  emailField.placeholder = fixedT('entities.field.contact_us_email_placeholder')
  const recaptcha = createRecaptcha(
    EntityTypeEnum.ContactUsRecaptcha,
    contactUs.id,
    contactUs.masterBlockId,
  )

  const attachments = createAttachments(contactUs.id, contactUs.masterBlockId)
  const contactUsButton = createContactUsButton(
    contactUs.id,
    fixedT('entities.button.contact_us_default_text'),
    contactUs.masterBlockId,
  )

  row.childIds = [column1.id, column2.id]
  contactUs.childIds = [
    row.id,
    subjectField.id,
    textArea.id,
    attachments.id,
    recaptcha.id,
    contactUsButton.id,
  ]
  column1.childIds = [firstNameField.id]
  column2.childIds = [emailField.id]

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, contactUs.id, position),
    },
    [contactUs.id]: contactUs,
    [row.id]: row,
    [column1.id]: column1,
    [column2.id]: column2,
    [subjectField.id]: subjectField,
    [textArea.id]: textArea,
    [emailField.id]: emailField,
    [firstNameField.id]: firstNameField,
    [attachments.id]: attachments,
    [recaptcha.id]: recaptcha,
    [contactUsButton.id]: contactUsButton,
  }
}

export function createMenuFromLibrary({
  createMenu,
  container,
  locale,
  position = 0,
}: {
  createMenu: (parentId: string, masterBlockId?: MasterBlockId) => MenuInterface
  container: EntityWithChildIdsMigrationType
  locale: string
  position?: number
}) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)

  const menu = createMenu(descendantEntity.id, descendantEntity.masterBlockId)

  menu.menuItems[0].text = fixedT('entity_settings.menu.item.home_placeholder')
  menu.menuItems[1].text = fixedT(
    'entity_settings.menu.item.contacts_placeholder',
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, menu.id, position),
    },
    [menu.id]: menu,
  }
}

export function createSurveyFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const survey = createSurvey(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  const {
    row,
    column1,
    structure: surveyStructure,
  } = createRowColumnLayout({
    parentEntity: survey,
    position,
  })

  row.padding = {
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  }
  row.mobilePadding = {
    paddingBottom: 5,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 5,
  }

  const emailPlaceholder = 'entities.field.email.default_placeholder'
  const firstNamePlaceholder = 'entities.field.first_name.default_placeholder'

  const fixedT = i18n.getFixedT(locale)

  const fieldEmail = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.Email,
    placeholder: emailPlaceholder,
  })
  const fieldFirstName = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.FirstName,
    placeholder: firstNamePlaceholder,
  })

  const button = createButtonNew(
    column1.id,
    fixedT('entities.button.default_text'),
    fixedT('entities.button.default_sub_text'),
    column1.masterBlockId,
  )
  button.redirectionType = RedirectionTypesEnum.none

  column1.childIds = [fieldEmail.id, fieldFirstName.id, button.id]

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, survey.id, position),
    },
    ...surveyStructure,
    [fieldEmail.id]: fieldEmail,
    [fieldFirstName.id]: fieldFirstName,
    [button.id]: button,
  }
}

export function createWebinarSessionVideoFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const webinarSessionVideo = createWebinarSessionVideo(
    locale,
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )
  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        webinarSessionVideo.id,
        position,
      ),
    },
    [webinarSessionVideo.id]: webinarSessionVideo,
  }
}

export function createWebinarCallToActionFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const fixedT = i18n.getFixedT(locale)

  const webinarCallToAction = createWebinarCallToAction(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  webinarCallToAction.text = fixedT(
    'entities.webinar_session_call_to_action.default_text',
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        webinarCallToAction.id,
        position,
      ),
    },
    [webinarCallToAction.id]: webinarCallToAction,
  }
}

/** @deprecated Use createDefaultEntityStructureNew instead */
export function createDefaultEntityStructure<T extends EntityInterface>({
  createEntity,
  container,
  locale,
  position = 0,
}: {
  createEntity: (parentId: string, masterBlockId?: MasterBlockId) => T
  container: EntityWithChildIdsMigrationType
  locale: string
  position?: number
}) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const newEntity = createEntity(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, newEntity.id, position),
    },
    [newEntity.id]: newEntity,
  }
}

export const createDefaultEntityStructureNew = <T extends EntityInterface>({
  createEntity,
  container,
  locale,
  position = 0,
}: {
  createEntity: (args: {
    parentId: string
    masterBlockId?: MasterBlockId
    locale: string
  }) => T
  container: EntityWithChildIdsMigrationType
  locale: string
  position?: number
}) => {
  return createDefaultEntityStructure({
    createEntity: (parentId: string, masterBlockId?: MasterBlockId) =>
      createEntity({ parentId, masterBlockId, locale }),
    container,
    locale,
    position,
  })
}

function createSectionLayout12(
  rootEntity: OldEntityWithChildIds,
  position: number,
  columnType?: ColumnTypeEnum,
) {
  const section = createSection(rootEntity.id, rootEntity.masterBlockId)
  const layout = createRowColumnLayout({
    parentEntity: section,
    position,
    columnType,
  })

  return {
    column: layout.column1,
    structure: {
      [rootEntity.id]: {
        ...rootEntity,
        childIds: insertChild(rootEntity.childIds, section.id, position),
      },
      ...layout.structure,
    },
  }
}

export function createRowLayout12(
  parentEntity: OldEntityWithChildIds | EntityWithChildIdsInterface,
  locale: string,
  position: number,
) {
  const row = createEntity(
    structureTypes.ROW,
    parentEntity.id,
    locale,
    parentEntity.masterBlockId,
  ) as OldEntityWithChildIds
  const column = createEntity(
    structureTypes.COLUMN_12,
    row.id,
    locale,
    row.masterBlockId,
  ) as OldEntityWithChildIds

  return {
    column,
    structure: {
      [parentEntity.id]: {
        ...parentEntity,
        childIds: insertChild(parentEntity.childIds, row.id, position),
      },
      [row.id]: { ...row, childIds: [column.id] },
      [column.id]: column,
    },
  }
}

export function createOptInRecaptchaFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const recaptcha = createRecaptcha(
    EntityTypeEnum.OptInRecaptcha,
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(descendantEntity.childIds, recaptcha.id, position),
    },
    [recaptcha.id]: recaptcha,
  }
}

export function createSectionFromLibrary(
  body: EntityWithChildIdsMigrationType,
  position = 0,
): EntitiesStructure {
  const section = createSection(body.id, body.masterBlockId)

  const { structure } = createRowColumnLayout({
    parentEntity: section,
    position,
  })

  return {
    [body.id]: {
      ...body,
      childIds: insertChild(body.childIds, section.id, position),
    },
    ...structure,
  }
}

export function createRowFromLibrary(
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
  columnType?: ColumnTypeEnum,
): EntitiesStructure {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
    columnType,
  )
  if (container.type === descendantEntity.type) {
    const { structure: layoutStructure } = createRowColumnLayout({
      parentEntity: descendantEntity as EntityWithChildIdsInterface,
      position,
      columnType,
    })
    return {
      ...structure,
      ...layoutStructure,
    }
  } else {
    return {
      ...structure,
      [descendantEntity.id]: descendantEntity,
    }
  }
}

export function createContentBoxFromLibrary(
  container: EntityWithChildIdsMigrationType,
  locale: string,
  position = 0,
) {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const contentBoxEntity = createContentBox(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )
  const fixedT = i18n.getFixedT(locale)
  contentBoxEntity.header.title = fixedT(
    'entities.content_box.default_header_title',
  )

  const { structure: layoutStructure } = createRowColumnLayout({
    parentEntity: contentBoxEntity,
    position,
  })

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        contentBoxEntity.id,
        position,
      ),
    },
    [contentBoxEntity.id]: contentBoxEntity,
    ...layoutStructure,
  }
}

export const createCheckboxFromLibrary = (
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
) => {
  const fixedT = i18n.getFixedT(locale)

  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const checkboxEntity = createCheckbox(
    descendantEntity.id,
    fixedT('entities.explicit_consent.default_text'),
    descendantEntity.masterBlockId,
  )
  checkboxEntity.errorText = fixedT('settings_options.checkbox_message')

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        checkboxEntity.id,
        position,
      ),
    },
    [checkboxEntity.id]: checkboxEntity,
  }
}

export const createCalendarFromLibrary = (
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
) => {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)

  const calendarEntity = createBookingCalendar({
    t: fixedT,
    parentId: descendantEntity.id,
    masterBlockId: descendantEntity.masterBlockId,
  })

  const { row, column1 } = createRowColumnLayout({
    parentEntity: calendarEntity,
    position,
  })

  const formDescriptionText = fixedT(
    'entities.calendar.form_description_default_text',
  )
  const formDescription = createText(
    column1.id,
    column1.masterBlockId,
    formDescriptionText,
  )
  formDescription.textAlign = 'left'
  formDescription.margin = {
    marginBottom: 20,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  }

  const firstNameField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.FirstName,
    placeholder: fixedT('entities.field.first_name.default_placeholder'),
    margin: {
      marginBottom: 15,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
    },
    mobileMargin: {
      marginTop: 0,
      marginBottom: 10,
      marginLeft: 0,
      marginRight: 0,
    },
  })

  const lastNameField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.LastName,
    placeholder: fixedT('entities.field.last_name.default_placeholder'),
    margin: {
      marginBottom: 15,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
    },
    mobileMargin: {
      marginTop: 0,
      marginBottom: 10,
      marginLeft: 0,
      marginRight: 0,
    },
  })

  const phoneField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.PhoneNumber,
    placeholder: fixedT('entity_settings.phone.title'),
    optional: true,
    margin: {
      marginBottom: 15,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
    },
    mobileMargin: {
      marginTop: 0,
      marginBottom: 10,
      marginLeft: 0,
      marginRight: 0,
    },
  })

  const emailField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.Email,
    placeholder: fixedT('entities.field.email.default_placeholder'),
    margin: {
      marginBottom: 15,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
    },
    mobileMargin: {
      marginTop: 0,
      marginBottom: 10,
      marginLeft: 0,
      marginRight: 0,
    },
  })

  column1.childIds = [
    formDescription.id,
    firstNameField.id,
    lastNameField.id,
    emailField.id,
    phoneField.id,
  ]
  row.childIds = [column1.id]

  const mainRow = findEntityByType(structure, EntityTypeEnum.Row)

  if (mainRow) {
    mainRow.padding = {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    }
    mainRow.mobilePadding = {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    }
  }

  calendarEntity.childIds = [row.id]

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        calendarEntity.id,
        position,
      ),
    },
    [calendarEntity.id]: calendarEntity,

    [row.id]: row,
    [column1.id]: column1,
    [formDescription.id]: formDescription,
    [firstNameField.id]: firstNameField,
    [lastNameField.id]: lastNameField,
    [phoneField.id]: phoneField,
    [emailField.id]: emailField,
  }
}

export const createShippingFeesFromLibrary = (
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
) => {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const shippingEntity = createShippingFees(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        shippingEntity.id,
        position,
      ),
    },
    [shippingEntity.id]: shippingEntity,
  }
}

export const createPaymentButtonFromLibrary = (
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
) => {
  const fixedT = i18n.getFixedT(locale)

  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const paymentButton = createPaymentButton(
    descendantEntity.id,
    descendantEntity.masterBlockId,
    fixedT('entities.payment_button.default_loading_text'),
    fixedT('entities.payment_button.default_text'),
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        paymentButton.id,
        position,
      ),
    },
    [paymentButton.id]: paymentButton,
  }
}

export function createContainerStructure(
  containerEntity:
    | OldEntityWithChildIds
    | EntityInnerItemInterface
    | EntityWithChildIdsInterface
    | BaseEntityWithChildIdsInterface,
  locale: string,
  position: number,
  columnType?: ColumnTypeEnum,
) {
  let structure: EntitiesStructure
  let descendantEntity:
    | OldEntityWithChildIds
    | EntityInnerItemInterface
    | EntityWithChildIdsInterface
    | BaseEntityWithChildIdsInterface
  switch (containerEntity.type) {
    case structureTypes.BODY:
    case EntityTypeEnum.Body:
    case structureTypes.BLOG_PAGE_BODY:
    case EntityTypeEnum.BlogPageBody:
    case structureTypes.BLOG_POST_BODY:
    case EntityTypeEnum.BlogPostBody:
    case structureTypes.BLOG_POST_LAYOUT_BODY:
    case EntityTypeEnum.BlogPostLayoutBody: {
      const layout = createSectionLayout12(
        containerEntity as OldEntityWithChildIds,
        position,
        columnType,
      )
      descendantEntity = layout.column
      structure = layout.structure
      break
    }

    case EntityTypeEnum.Section:
    case EntityTypeEnum.Row:
    case EntityTypeEnum.Survey:
    case structureTypes.SURVEY:
    case EntityTypeEnum.InlineBody:
    case EntityTypeEnum.RemotePopupBody:
    case EntityTypeEnum.Popup:
    case structureTypes.SECTION: {
      const layout = createRowColumnLayout({
        parentEntity: containerEntity as EntityWithChildIdsInterface,
        position,
        columnType,
      })
      descendantEntity = layout.column1
      structure = layout.structure
      break
    }
    case structureTypes.ROW: {
      const layout = createRowLayout12(
        containerEntity as OldEntityWithChildIds,
        locale,
        position,
      )
      descendantEntity = layout.column
      structure = layout.structure
      break
    }
    case EntityTypeEnum.FaqItem:
    case EntityTypeEnum.CarouselItem:
    case structureTypes.CONTENT_BOX:
    case EntityTypeEnum.ContentBox:
    case structureTypes.COLUMN:
    case EntityTypeEnum.Column:
    case structureTypes.ORDER_BUMP:
    case EntityTypeEnum.TwoStepPaymentForm:
    case EntityTypeEnum.TwoStepPaymentFormStepOptIn:
    case EntityTypeEnum.TwoStepPaymentFormStepPayment:
    case structureTypes.TWO_STEP_PAYMENT_FORM:
    case structureTypes.TWO_STEP_PAYMENT_FORM_STEP_OPT_IN:
    case structureTypes.TWO_STEP_PAYMENT_FORM_STEP_PAYMENT:
    case EntityTypeEnum.ContactUs: {
      descendantEntity = containerEntity
      structure = { [descendantEntity.id]: descendantEntity }
      break
    }
    case EntityTypeEnum.WebsiteFooter:
    case EntityTypeEnum.WebsiteHeader:
    case EntityTypeEnum.WebsitePageBody: {
      const layout = createRowColumnLayout({
        parentEntity: containerEntity as EntityWithChildIdsInterface,
        position,
        columnType,
      })
      descendantEntity = layout.column1
      structure = layout.structure
      break
    }
    default:
      throw new Error(`container entity unknown ${containerEntity.type}`)
  }

  return { structure, descendantEntity }
}

export const renewEntityHtmlAttrId = (
  entity: EntityInterface | OldEntityInterface,
) => {
  if (entity.type in EntityTypeEnum) {
    return {
      ...entity,
      htmlAttrId: generateHtmlAttrId(entity.type),
    }
  } else {
    const updatedEntity = entity as OldEntityInterface
    return {
      ...updatedEntity,
      options: {
        ...updatedEntity.options,
        attrId: generateHtmlAttrId(updatedEntity.type),
      },
    }
  }
}

export function createEntity(
  type: EntityTypeEnum | string,
  parentId: string,
  locale: string,
  masterBlockId?: MasterBlockId,
  styles?: Record<string, string>,
  options?: Record<string, string>,
  mobileStyles?: Record<string, string>,
) {
  // biome-ignore lint/suspicious/noExplicitAny: migrated from eslint
  let entity: EntityInterface | any
  switch (type) {
    case EntityTypeEnum.Carousel:
      entity = createCarousel(parentId, masterBlockId)
      break
    default:
      entity = createStructureOld.createEntity(
        locale,
        type,
        parentId,
        styles,
        mobileStyles,
        options,
        {},
        // @ts-expect-error @TODO: need a description
        masterBlockId,
      )
  }

  return entity
}
